import React, { useCallback, useEffect, useRef, useState } from 'react'

import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { useMediaQuery } from '../../hooks/useMediaQuery'
import { breakpoints } from '../../theme'
import ModalHeader from './ModalHeader'

const popUp = keyframes`
    0% {
        transform: translateY(1rem);
    }
    100% {
        transform: translateY(0);
    }
`

const DEFAULT_MAX_WIDTH = '39rem'

const Container = styled.div<{ disableFullScreenModal?: boolean; maxWidth?: string; isDesktop?: boolean }>`
  width: 100%;
  margin: 0 auto;
  animation: ${popUp} 200ms forwards;
  background: ${props => props.theme.arcadeColors.component.modalBackground};
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  outline: 1rem solid ${props => props.theme.arcadeColors.component.modalBackground};

  ${props =>
    (props.disableFullScreenModal || props.isDesktop) &&
    css`
      max-width: ${props.maxWidth ?? DEFAULT_MAX_WIDTH};
      height: auto;
      max-height: 80vh;
      margin-top: 10vh;
      margin-bottom: 10vh;
      border-radius: 1.5rem;
      overflow: hidden;
      box-shadow: ${props.theme.dropShadows.dialog};
      outline: 0;

      /* Adjust max-width on mobile to create spacing between Container and viewport edge */
      ${!props.isDesktop &&
      css`
        max-width: calc(${props.maxWidth ?? '100%'} - ${props.theme.spacing.m} * 2);
      `}
    `}
`

const PinnedContentContainer = styled.div<{ disableFullScreenModal?: boolean; isDesktop?: boolean }>`
  padding: ${({ theme: { spacing } }) => `${spacing.m} ${spacing.l}`};
  padding-top: ${({ theme }) => theme.spacing.xs};

  ${props =>
    (props.disableFullScreenModal || props.isDesktop) &&
    `
    padding: ${props.theme.spacing.m} 4rem;
    padding-top: ${props.theme.spacing.xs};
  `}
`

const ScrollableContent = styled.div<{
  contentPadding?: string
  pinnedContent?: unknown
  disableFullScreenModal?: boolean
  isDesktop?: boolean
}>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${({ contentPadding, theme: { spacing } }) => contentPadding ?? `${spacing.m} ${spacing.xl} 2.5rem`};
  position: relative;
  flex-grow: 1;

  ${props => props.theme.typography.body}

  ${props => {
    if (props.disableFullScreenModal || props.isDesktop) {
      if (props.contentPadding) {
        return `padding: ${props.contentPadding};`
      }
      return `
        padding-top: 0;
        padding-bottom: ${props.pinnedContent ? props.theme.spacing.l : props.theme.spacing.defaultContent};
        padding-left: ${props.theme.spacing.headerSpacing};
        padding-right: ${props.theme.spacing.headerSpacing};
      `
    }
    return ''
  }}
`

const ShadowPosition = styled.div`
  position: relative;
  height: 0;
  pointer-events: none;
`

const ShadowClip = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1rem;
  overflow: hidden;
`

const Shadow = styled.div<{ active: boolean }>`
  width: 100%;
  left: 0;
  right: 0;
  top: -5rem;
  height: 5rem;
  position: absolute;
  transition: opacity 100ms;
  box-shadow: ${props => props.theme.dropShadows.topBar};
  opacity: ${props => (props.active ? 1 : 0)};
`

const ScrollDetector = styled.div`
  width: 1px;
  height: 1px;
  position: absolute;
  top: 0;
  left: 0;
`

export interface ModalProps {
  embedded?: boolean
  contentPadding?: string
  disableClose?: boolean
  disableFullScreenModal?: boolean
  maxWidth?: string
  onClose?: () => void
  ariaLabel?: string
  pinnedContent?: React.ReactNode
  scrollable?: boolean
  showClose?: boolean
  children: React.ReactNode
}

function useScrolled() {
  const target = useRef<HTMLElement | null>()
  const root = useRef<HTMLElement | null>()
  const [hasScrolled, setScrolled] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(entries => setScrolled(!entries[0].isIntersecting), {
      root: root.current,
    })
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    observer.observe(target.current!)
    return () => observer.disconnect()
  }, [])

  return { hasScrolled, root, target }
}

const Modal: React.FC<ModalProps> = ({
  children,
  disableClose,
  disableFullScreenModal,
  embedded,
  contentPadding,
  maxWidth,
  ariaLabel,
  onClose = () => null,
  showClose = true,
  pinnedContent,
  scrollable = true,
}) => {
  const { hasScrolled, target, root } = useScrolled()
  const isDesktop = useMediaQuery(breakpoints.tablet)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !disableClose) {
        onClose()
      }
    },
    [disableClose, onClose]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  return (
    <Container
      disableFullScreenModal={disableFullScreenModal}
      maxWidth={maxWidth}
      isDesktop={isDesktop}
      ref={ref => {
        root.current = ref
      }}
      onClick={event => event.stopPropagation()}
      aria-modal="true"
      aria-label={ariaLabel}
      role="dialog"
    >
      {showClose && !embedded && (
        <ModalHeader
          disableClose={disableClose}
          onClose={onClose}
          disableFullScreenModal={disableFullScreenModal}
          isDesktop={isDesktop}
        />
      )}
      {pinnedContent && (
        <PinnedContentContainer disableFullScreenModal={disableFullScreenModal} isDesktop={isDesktop}>
          {pinnedContent}
        </PinnedContentContainer>
      )}
      <ShadowPosition>
        <ShadowClip>
          <Shadow active={hasScrolled} />
        </ShadowClip>
      </ShadowPosition>
      <ScrollableContent
        contentPadding={contentPadding}
        pinnedContent={pinnedContent}
        disableFullScreenModal={disableFullScreenModal}
        isDesktop={isDesktop}
      >
        <ScrollDetector
          ref={ref => {
            target.current = ref
          }}
        />
        <div style={{ flexGrow: 1, overflowY: scrollable ? undefined : 'hidden' }}>{children}</div>
      </ScrollableContent>
    </Container>
  )
}

export default Modal
